
import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({ firstName: '', email: '', experience: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    const form = e.currentTarget;
    const formDataToSend = new FormData(form);
    try {
      const response = await fetch('https://readdy.ai/api/form/d6g9ka0r225iqdhs0mn0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataToSend as any).toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ firstName: '', email: '', experience: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const briefings = [
    {
      location: 'Birkenhead, Merseyside',
      type: 'Residential Development',
      description:
        "A strategic residential conversion in Birkenhead's regeneration zone, offering exposure to the Liverpool City Region's growth trajectory with strong rental demand fundamentals.",
      returnRate: '8.5% p.a.',
      term: '24 months',
      structure: 'Asset-backed',
      image:
        'https://public.readdy.ai/ai/img_res/8fa20f1840d5eac6b290a1ead4ddfb7c.jpg',
    },
    {
      location: 'Nottingham City Centre',
      type: 'Refurbishment Opportunity',
      description:
        "A value-add opportunity in Nottingham's thriving city centre, targeting the professional rental market with high-specification residential units in a prime location.",
      returnRate: '9.1% p.a.',
      term: '18 months',
      structure: 'Asset-backed',
      image:
        'https://readdy.ai/api/search-image?query=Nottingham%20city%20centre%20UK%20property%20investment%20refurbishment%20Victorian%20architecture%20modern%20residential%20conversion%20professional%20real%20estate%20photography%20clean%20neutral%20background&width=800&height=560&seq=nottingham01&orientation=landscape',
    },
    {
      location: 'East Staffordshire',
      type: 'Mixed-Use Portfolio',
      description:
        "A diversified portfolio combining residential and commercial assets in East Staffordshire, benefiting from the region's economic stability and transport connectivity.",
      returnRate: '7.2% p.a.',
      term: '36 months',
      structure: 'Asset-backed',
      image:
        'https://readdy.ai/api/search-image?query=East%20Staffordshire%20UK%20mixed%20use%20commercial%20residential%20property%20portfolio%20town%20centre%20architecture%20investment%20real%20estate%20photography%20neutral%20simple%20background%20professional&width=800&height=560&seq=staffs01&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Inter', sans-serif", backgroundColor: '#F0EDE8' }}>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300" style={{ backgroundColor: '#1C2B2B', borderBottom: '1px solid #2E4040' }}>
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <img
            src="https://static.readdy.ai/image/ac9212d29167580d375a6988ed64ba07/b4fcdfa453927c1e26a82c1b4f2dbefd.png"
            alt="Rock Harbour Invest"
            className="h-9 brightness-0 invert"
          />
          <span className="text-xs tracking-widest uppercase" style={{ color: '#A8B8A8', fontFamily: "'Inter', sans-serif" }}>
            UK Property Investment
          </span>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-0 overflow-hidden" style={{ minHeight: '92vh' }}>
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=London%20UK%20cityscape%20aerial%20view%20at%20dusk%20golden%20hour%20dramatic%20sky%20Thames%20river%20financial%20district%20skyscrapers%20architectural%20photography%20moody%20atmospheric%20investment%20finance%20professional&width=1600&height=900&seq=hero01&orientation=landscape"
            alt="UK cityscape"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(15,25,25,0.72) 0%, rgba(15,25,25,0.55) 50%, rgba(15,25,25,0.80) 100%)' }}></div>
        </div>
        <div className="relative z-10 flex flex-col items-center justify-center text-center px-6" style={{ minHeight: '92vh', paddingTop: '80px' }}>
          <p className="text-xs tracking-[0.3em] uppercase mb-8" style={{ color: '#A8C4A2', fontFamily: "'Inter', sans-serif" }}>
            Curated UK Real Estate Intelligence
          </p>
          <h1 className="font-serif mb-8 leading-tight" style={{ fontFamily: "'Crimson Pro', serif", fontSize: 'clamp(2.8rem, 6vw, 5.5rem)', color: '#F0EDE8', maxWidth: '900px' }}>
            UK Property Investment and Market Insight, Delivered.
          </h1>
          <p className="text-lg leading-relaxed" style={{ color: '#C8D4C8', maxWidth: '640px', fontFamily: "'Inter', sans-serif" }}>
            Rock Harbour Invest specialises in curated UK real estate opportunities and research, designed to help investors build long-term portfolio value.
          </p>
          <div className="mt-12 w-px h-16" style={{ background: 'linear-gradient(to bottom, #A8C4A2, transparent)' }}></div>
        </div>
      </section>

      {/* Why Rock Harbour Invest */}
      <section className="py-24 px-6" style={{ backgroundColor: '#F0EDE8' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-widest uppercase mb-4" style={{ color: '#6B8C6B', fontFamily: "'Inter', sans-serif" }}>
              Our Approach
            </p>
            <h2 className="font-serif" style={{ fontFamily: "'Crimson Pro', serif", fontSize: 'clamp(2rem, 4vw, 3rem)', color: '#1C2B2B' }}>
              Why Rock Harbour Invest
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'ri-line-chart-line', title: 'Tailored Investment Insight', desc: 'Research and analysis aligned with your investment objectives and risk profile.' },
              { icon: 'ri-building-2-line', title: 'Curated UK Property Opportunities', desc: 'Access to carefully vetted real estate investments across the United Kingdom.' },
              { icon: 'ri-bar-chart-box-line', title: 'Transparent & Data-Driven Analysis', desc: 'Clear, evidence-based reporting with no hidden agendas or conflicts of interest.' },
              { icon: 'ri-user-heart-line', title: 'Personalised Investor Support', desc: 'Dedicated guidance to help you navigate the UK property investment landscape.' },
            ].map((item, i) => (
              <div key={i} className="p-8 rounded-lg" style={{ backgroundColor: '#E8E4DE', border: '1px solid #D4CFC8' }}>
                <div className="w-10 h-10 flex items-center justify-center mb-5 rounded-md" style={{ backgroundColor: '#1C2B2B' }}>
                  <i className={`${item.icon} text-lg`} style={{ color: '#A8C4A2' }}></i>
                </div>
                <h3 className="font-serif mb-3" style={{ fontFamily: "'Crimson Pro', serif", fontSize: '1.2rem', color: '#1C2B2B' }}>
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#5A6A5A' }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Briefings — Image-focused cards */}
      <section className="py-24 px-6" style={{ backgroundColor: '#1C2B2B' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-widest uppercase mb-4" style={{ color: '#A8C4A2', fontFamily: "'Inter', sans-serif" }}>
              Newsletter Content
            </p>
            <h2 className="font-serif mb-6" style={{ fontFamily: "'Crimson Pro', serif", fontSize: 'clamp(2rem, 4vw, 3rem)', color: '#F0EDE8' }}>
              Recent Investment Briefings
            </h2>
            <p className="text-base leading-relaxed" style={{ color: '#8A9E8A', maxWidth: '600px', margin: '0 auto' }}>
              These examples illustrate the kinds of structured property insights and opportunity summaries shared with our newsletter audience.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {briefings.map((b, i) => (
              <div key={i} className="rounded-xl overflow-hidden flex flex-col" style={{ backgroundColor: '#243535', border: '1px solid #2E4040' }}>
                <div className="relative overflow-hidden" style={{ height: '240px' }}>
                  <img src={b.image} alt={b.location} className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(15,25,25,0.7) 0%, transparent 60%)' }}></div>
                  <div className="absolute bottom-4 left-4">
                    <span className="text-xs tracking-widest uppercase px-3 py-1 rounded-full" style={{ backgroundColor: 'rgba(168,196,162,0.2)', color: '#A8C4A2', border: '1px solid rgba(168,196,162,0.3)', fontFamily: "'Inter', sans-serif" }}>
                      {b.type}
                    </span>
                  </div>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <p className="text-xs tracking-widest uppercase mb-3" style={{ color: '#6B8C6B', fontFamily: "'Inter', sans-serif" }}>
                    {b.location}
                  </p>
                  <p className="text-sm leading-relaxed mb-6 flex-1" style={{ color: '#A8B8A8' }}>
                    {b.description}
                  </p>
                  <div className="pt-5" style={{ borderTop: '1px solid #2E4040' }}>
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div>
                        <p style={{ color: '#6B8C6B', fontFamily: "'Inter', sans-serif" }} className="mb-1">
                          Target Return
                        </p>
                        <p className="font-medium" style={{ color: '#F0EDE8' }}>
                          {b.returnRate}
                        </p>
                      </div>
                      <div>
                        <p style={{ color: '#6B8C6B', fontFamily: "'Inter', sans-serif" }} className="mb-1">
                          Term
                        </p>
                        <p className="font-medium" style={{ color: '#F0EDE8' }}>
                          {b.term}
                        </p>
                      </div>
                      <div>
                        <p style={{ color: '#6B8C6B', fontFamily: "'Inter', sans-serif" }} className="mb-1">
                          Structure
                        </p>
                        <p className="font-medium" style={{ color: '#F0EDE8' }}>
                          {b.structure}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-center mt-10" style={{ color: '#4A6060' }}>
            *Illustrative only. Capital at risk.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6" style={{ backgroundColor: '#F0EDE8' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-widest uppercase mb-4" style={{ color: '#6B8C6B', fontFamily: "'Inter', sans-serif" }}>
              The Process
            </p>
            <h2 className="font-serif" style={{ fontFamily: "'Crimson Pro', serif", fontSize: 'clamp(2rem, 4vw, 3rem)', color: '#1C2B2B' }}>
              How It Works
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { n: '01', title: 'Discovery', desc: 'Understanding your investment goals, risk tolerance and portfolio requirements.' },
              { n: '02', title: 'Research & Analysis', desc: 'Market-backed insight and due diligence on UK property opportunities.' },
              { n: '03', title: 'Opportunity Summaries', desc: 'Structured briefing content delivered through our newsletter platform.' },
              { n: '04', title: 'Ongoing Insight', desc: 'Continued newsletters and market commentary to support your investment decisions.' },
            ].map((step, i) => (
              <div key={i} className="relative p-8 rounded-lg" style={{ backgroundColor: '#E8E4DE', border: '1px solid #D4CFC8' }}>
                <p className="font-serif mb-4" style={{ fontFamily: "'Crimson Pro', serif", fontSize: '2.5rem', color: '#C8C0B4', lineHeight: 1 }}>
                  {step.n}
                </p>
                <h3 className="font-serif mb-3" style={{ fontFamily: "'Crimson Pro', serif", fontSize: '1.15rem', color: '#1C2B2B' }}>
                  {step.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#5A6A5A' }}>
                  {step.desc}
                </p>
                {i < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-px" style={{ backgroundColor: '#C8C0B4' }}></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Market Insight */}
      <section className="py-24 px-6" style={{ backgroundColor: '#E8E4DE' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-widest uppercase mb-4" style={{ color: '#6B8C6B', fontFamily: "'Inter', sans-serif" }}>
              Editorial
            </p>
            <h2 className="font-serif" style={{ fontFamily: "'Crimson Pro', serif", fontSize: 'clamp(2rem, 4vw, 3rem)', color: '#1C2B2B' }}>
              Market Insight
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'UK Residential Market in 2025 – Confidence Returns', desc: 'Analysis of emerging trends in the UK housing market as buyer confidence stabilises following interest rate adjustments and economic recalibration.' },
              { title: "Nottingham's Resilience & Growth Potential", desc: "A deep dive into Nottingham's property fundamentals, examining demographic shifts, infrastructure investment and rental market dynamics." },
              { title: 'Regional Investment Opportunities Beyond London', desc: "Exploring value and yield opportunities in the UK's regional cities, where affordability and economic growth create compelling investment cases." },
            ].map((item, i) => (
              <div key={i} className="p-8 rounded-lg" style={{ backgroundColor: '#F0EDE8', border: '1px solid #D4CFC8' }}>
                <div className="w-8 h-0.5 mb-6" style={{ backgroundColor: '#6B8C6B' }}></div>
                <h3 className="font-serif mb-4" style={{ fontFamily: "'Crimson Pro', serif", fontSize: '1.2rem', color: '#1C2B2B', lineHeight: 1.4 }}>
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#5A6A5A' }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Sign-up */}
      <section className="py-24 px-6 relative overflow-hidden" style={{ backgroundColor: '#1C2B2B' }}>
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=UK%20property%20investment%20abstract%20architectural%20texture%20dark%20moody%20background%20pattern%20minimal%20geometric&width=1600&height=800&seq=nlbg01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs tracking-widest uppercase mb-4" style={{ color: '#A8C4A2', fontFamily: "'Inter', sans-serif" }}>
              Newsletter
            </p>
            <h2 className="font-serif mb-5" style={{ fontFamily: "'Crimson Pro', serif", fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)', color: '#F0EDE8' }}>
              Stay Informed with Timely Insights and Investment Briefings.
            </h2>
            <p className="text-base leading-relaxed" style={{ color: '#8A9E8A' }}>
              Receive regular investment insights, curated property briefings and market commentary directly to your inbox.
            </p>
          </div>

          <form
            id="newsletter-form-secondary"
            onSubmit={handleSubmit}
            data-readdy-form
            className="rounded-xl p-10"
            style={{ backgroundColor: '#F0EDE8' }}
          >
            <div className="space-y-5">
              <div>
                <label htmlFor="firstName2" className="block text-xs font-medium tracking-wider uppercase mb-2" style={{ color: '#5A6A5A', fontFamily: "'Inter', sans-serif" }}>
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName2"
                  name="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-md text-sm focus:outline-none"
                  style={{ border: '1px solid #C8C0B4', backgroundColor: '#FAFAF8', color: '#1C2B2B', fontFamily: "'Inter', sans-serif" }}
                />
              </div>
              <div>
                <label htmlFor="email2" className="block text-xs font-medium tracking-wider uppercase mb-2" style={{ color: '#5A6A5A', fontFamily: "'Inter', sans-serif" }}>
                  Email Address
                </label>
                <input
                  type="email"
                  id="email2"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-md text-sm focus:outline-none"
                  style={{ border: '1px solid #C8C0B4', backgroundColor: '#FAFAF8', color: '#1C2B2B', fontFamily: "'Inter', sans-serif" }}
                />
              </div>
              <div>
                <label htmlFor="experience2" className="block text-xs font-medium tracking-wider uppercase mb-2" style={{ color: '#5A6A5A', fontFamily: "'Inter', sans-serif" }}>
                  Investment Experience
                </label>
                <select
                  id="experience2"
                  name="experience"
                  value={formData.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  required
                  className="w-full px-4 py-3 rounded-md text-sm focus:outline-none cursor-pointer"
                  style={{ border: '1px solid #C8C0B4', backgroundColor: '#FAFAF8', color: '#1C2B2B', fontFamily: "'Inter', sans-serif" }}
                >
                  <option value="">Select your experience level</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="experienced">Experienced</option>
                  <option value="professional">Professional</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 rounded-md text-sm font-medium tracking-wider uppercase transition-opacity duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: '#1C2B2B', color: '#A8C4A2', fontFamily: "'Inter', sans-serif" }}
              >
                {isSubmitting ? 'Submitting…' : 'Subscribe to Newsletter'}
              </button>
              {submitStatus === 'success' && (
                <p className="text-center text-sm" style={{ color: '#6B8C6B' }}>
                  Thank you for subscribing! Please check your inbox.
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="text-center text-sm text-red-600">Something went wrong. Please try again.</p>
              )}
            </div>
            <p className="text-xs text-center mt-6 leading-relaxed" style={{ color: '#8A8A7A' }}>
              Capital at risk. Information provided is general and does not constitute financial advice.
            </p>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6" style={{ backgroundColor: '#0F1A1A' }}>
        <div className="max-w-6xl mx-auto text-center">
          <img
            src="https://static.readdy.ai/image/ac9212d29167580d375a6988ed64ba07/b4fcdfa453927c1e26a82c1b4f2dbefd.png"
            alt="Rock Harbour Invest"
            className="h-8 mx-auto mb-6 brightness-0 invert opacity-70"
          />
          <p className="text-sm mb-3" style={{ color: '#6B8C6B', fontFamily: "'Inter', sans-serif" }}>
            Rock Harbour Invest • United Kingdom
          </p>
          <p className="text-xs leading-relaxed mb-6" style={{ color: '#3A5050', maxWidth: '560px', margin: '0 auto 1.5rem', fontFamily: "'Inter', sans-serif" }}>
            This site is informational only and does not constitute financial advice. Capital at risk.
          </p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-xs transition-colors duration-200 cursor-pointer"
            style={{ color: '#3A5050', fontFamily: "'Inter', sans-serif" }}
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
